/**
 * Inventory types for HiponHero Web Dashboard
 *
 * The API returns decimal values as strings (Pydantic Decimal serialization).
 * Use toNumber() from @/lib/number for arithmetic operations.
 */

// ============================================================================
// Template types (global catalog)
// ============================================================================

export interface InventoryTemplate {
  id: string;
  name: string;
  category: string;
  unit: string;
  default_reorder_point: number | string;
  suggested_unit_cost: number | string | null;
  is_active: boolean;
}

// ============================================================================
// Item types (farm-level)
// ============================================================================

export interface InventoryItemStock {
  quantity: number | string;
  status: 'ok' | 'low' | 'out';
  last_transaction_at: string | null;
}

export interface InventoryItem {
  id: string;
  farm_id: string;
  name: string;
  category: string;
  unit: string;
  item_code: string | null;
  aliases: string[];
  reorder_point: number | string;
  unit_cost: number | string | null;
  sku: string | null;
  is_active: boolean;
  template_id: string | null;
  template_update_available: boolean;
  stock: InventoryItemStock | null;
  created_at: string;
  last_modified: string;
  deleted_at: string | null;
}

// ============================================================================
// Stock types
// ============================================================================

export interface StockItem {
  item_id: string;
  item_name: string;
  category: string;
  unit: string;
  quantity: number | string;
  reorder_point: number | string;
  status: 'ok' | 'low' | 'out';
  unit_cost: number | string | null;
  total_value: number | string | null;
  last_transaction_at: string | null;
}

export interface StockSummary {
  total_items: number;
  low_stock_count: number;
  out_of_stock_count: number;
  total_value: number | string;
}

export interface StockOverview {
  items: StockItem[];
  summary: StockSummary;
}

export interface LowStockItem {
  item_id: string;
  item_name: string;
  quantity: number | string;
  reorder_point: number | string;
  shortage: number | string;
  unit: string;
  last_receipt_at: string | null;
  avg_daily_consumption: number | string | null;
}

// ============================================================================
// Transaction types
// ============================================================================

export type TransactionType =
  | 'receipt'
  | 'consumption'
  | 'adjustment'
  | 'return'
  | 'disposal'
  | 'transfer';

export interface InventoryTransaction {
  id: string;
  item_id: string;
  item_name: string | null;
  transaction_type: TransactionType | string;
  quantity: number | string;
  quantity_before: number | string;
  quantity_after: number | string;
  reference_type: string | null;
  reference_id: string | null;
  cycle_id: string | null;
  unit_cost_at_time: number | string | null;
  total_cost: number | string | null;
  supplier: string | null;
  notes: string | null;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
}

// ============================================================================
// Request types (for mutations)
// ============================================================================

export interface CreateItemRequest {
  name: string;
  category: string;
  unit: string;
  item_code?: string;
  aliases?: string[];
  reorder_point?: number;
  unit_cost?: number;
  sku?: string;
}

export interface UpdateItemRequest {
  name?: string;
  category?: string;
  unit?: string;
  item_code?: string;
  aliases?: string[];
  reorder_point?: number;
  unit_cost?: number;
  sku?: string;
  is_active?: boolean;
}

export interface ReceiveStockRequest {
  item_id: string;
  quantity: number;
  unit_cost?: number;
  supplier?: string;
  batch_number?: string;
  expiry_date?: string;
  notes?: string;
}

export interface AdjustStockRequest {
  item_id: string;
  new_quantity?: number;
  quantity_delta?: number;
  reason: string;
  notes?: string;
}

export interface ImportTemplatesRequest {
  template_ids: string[];
  overrides?: Record<string, { reorder_point?: number; unit_cost?: number }>;
}

export interface ImportTemplatesResult {
  imported: InventoryItem[];
  skipped: Array<{ template_id: string; reason: string }>;
}

// ============================================================================
// Category constants
// ============================================================================

export const INVENTORY_CATEGORIES = [
  { value: 'feed', label: 'Feed' },
  { value: 'probiotic', label: 'Probiotics' },
  { value: 'chemical', label: 'Chemicals' },
  { value: 'water_treatment', label: 'Water Treatment' },
  { value: 'feed_additive', label: 'Feed Additives' },
  { value: 'supply', label: 'Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
] as const;

export const STOCK_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'ok', label: 'OK' },
  { value: 'low', label: 'Low Stock' },
  { value: 'out', label: 'Out of Stock' },
] as const;

export const TRANSACTION_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'consumption', label: 'Consumption' },
  { value: 'adjustment', label: 'Adjustment' },
  { value: 'return', label: 'Return' },
  { value: 'disposal', label: 'Disposal' },
  { value: 'transfer', label: 'Transfer' },
] as const;
